import { MongoObjectIdSchema } from '@terra/shared-utils';
import { z } from 'zod';

export const ReassociateHarEntriesExistingTargetSchema = z.object({
  kind: z.literal('existing'),
  endpointId: MongoObjectIdSchema,
});

export const ReassociateHarEntriesCreateItemSchema = z.object({
  harEntryId: MongoObjectIdSchema,
  path: z.string().min(1),
});

export const ReassociateHarEntriesCreateTargetSchema = z
  .object({
    kind: z.literal('createFromHars'),
    path: z.string().min(1).optional(),
    items: z.array(ReassociateHarEntriesCreateItemSchema).min(1).optional(),
  })
  .superRefine((value, ctx) => {
    if (!value.path && !value.items) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'path or items is required',
        path: ['path'],
      });
    }
  });

export const ReassociateHarEntriesTargetSchema = z.discriminatedUnion('kind', [
  ReassociateHarEntriesExistingTargetSchema,
  ReassociateHarEntriesCreateTargetSchema,
]);

export const ReassociateHarEntriesInputSchema = z.object({
  id: MongoObjectIdSchema,
  harEntryIds: z.array(MongoObjectIdSchema).min(1),
  target: ReassociateHarEntriesTargetSchema,
});

export const ReassociateHarEntriesSuccessSchema = z.object({
  sourceEndpointId: MongoObjectIdSchema,
  targetEndpointId: MongoObjectIdSchema,
  targetEndpointIds: z.array(MongoObjectIdSchema).optional(),
  movedHarEntryIds: z.array(MongoObjectIdSchema),
  remountedSignalIds: z.array(MongoObjectIdSchema),
  createdTarget: z.boolean(),
  sourceDeleted: z.boolean(),
});

export const ReassociateHarEntriesConflictSchema = z.object({
  collision: z.enum(['active', 'archived']),
  suggestedEndpointId: MongoObjectIdSchema,
  method: z.string(),
  host: z.string(),
  path: z.string(),
  message: z.string(),
});

export type ReassociateHarEntriesTarget = z.infer<
  typeof ReassociateHarEntriesTargetSchema
>;
export type ReassociateHarEntriesInput = z.infer<
  typeof ReassociateHarEntriesInputSchema
>;
export type ReassociateHarEntriesSuccess = z.infer<
  typeof ReassociateHarEntriesSuccessSchema
>;
export type ReassociateHarEntriesConflict = z.infer<
  typeof ReassociateHarEntriesConflictSchema
>;

export class ReassociateHarEntriesConflictError extends Error {
  readonly collision: ReassociateHarEntriesConflict['collision'];
  readonly suggestedEndpointId: string;
  readonly method: string;
  readonly host: string;
  readonly path: string;

  constructor(conflict: ReassociateHarEntriesConflict) {
    super(conflict.message);
    this.name = 'ReassociateHarEntriesConflictError';
    this.collision = conflict.collision;
    this.suggestedEndpointId = conflict.suggestedEndpointId;
    this.method = conflict.method;
    this.host = conflict.host;
    this.path = conflict.path;
  }
}

export const isReassociateHarEntriesConflictError = (
  error: Error,
): error is ReassociateHarEntriesConflictError =>
  error instanceof ReassociateHarEntriesConflictError;
